import { useState, useEffect, useCallback } from 'react';
import { usePaywall } from '@/hooks/usePaywall';
import { hasActivePackageSubscription } from '@/utils/hasActivePackageSubscription';
import { findActivePackageSubscription } from '@/utils/findActivePackageSubscription';
import { findActiveAuthorSubscription } from '@/utils/findActiveAuthorSubscription';

import type { PaywallContextType } from '@/types/util.types';
import type { NotificationSettingsAuthor, SubscriptionSettings } from '@/types/models.types';

interface UseNotificationSettingsProps {
  authors: NotificationSettingsAuthor[];
}

interface UseNotificationSettingsReturn {
  subscriptionsSettings: SubscriptionSettings[];
  notificationStatusMap: Record<string, boolean>;
  loadingToggles: Record<string, boolean>;
  settingsLoading: boolean;
  userHasPackageSubscription: boolean;
  handleToggleChange: (authorId: number, checked: boolean) => Promise<void>;
}

export function useNotificationSettings({ authors }: UseNotificationSettingsProps): UseNotificationSettingsReturn {
  const [subscriptionsSettings, setSubscriptionsSettings] = useState<SubscriptionSettings[]>([]);
  const [notificationStatusMap, setNotificationStatusMap] = useState<Record<string, boolean>>({});
  const [loadingToggles, setLoadingToggles] = useState<Record<string, boolean>>({});
  const [settingsLoading, setSettingsLoading] = useState<boolean>(true);

  const { subscriptions, getSubscriptionsSettings, setSubscriptionSettings } = usePaywall() as PaywallContextType;
  const userHasPackageSubscription = hasActivePackageSubscription(subscriptions);

  /**
   * Fetches subscription settings from the backend and updates local state
   */
  const fetchSettings = useCallback(async () => {
    try {
      const settings = await getSubscriptionsSettings();
      setSubscriptionsSettings(settings);
    } catch (error) {
      console.error('Error fetching subscription settings:', error);
      alert('Failed to load notification settings');
    } finally {
      setSettingsLoading(false);
    }
  }, [getSubscriptionsSettings]);

  /**
   * Updates notification settings for a package subscription by modifying the overrides
   */
  function updatePackageSubscriptionSettings(
    packageSettings: SubscriptionSettings,
    authorId: number,
    checked: boolean
  ): SubscriptionSettings {
    return {
      ...packageSettings,
      notificationSettings: {
        ...packageSettings?.notificationSettings,
        overrides: {
          ...packageSettings?.notificationSettings?.overrides,
          [authorId]: checked,
        },
      },
    };
  }

  /**
   * Updates notification settings for a per-author subscription by modifying defaultEnabled
   */
  function updateAuthorSubscriptionSettings(
    authorSettings: SubscriptionSettings,
    checked: boolean
  ): SubscriptionSettings {
    return {
      ...authorSettings,
      notificationSettings: {
        ...authorSettings?.notificationSettings,
        defaultEnabled: checked,
      },
    };
  }

  /**
   * Handles saving updated settings to the backend and updating local state
   */
  async function saveUpdatedSettings(
    updatedSettings: SubscriptionSettings,
    authorId: number,
    checked: boolean
  ): Promise<void> {
    const subscriptionId = updatedSettings.id;
    await setSubscriptionSettings(subscriptionId, updatedSettings);
    updateStatusMap(authorId, checked);
    updateSubscriptionsSettings(updatedSettings);
  }

  /**
   * Updates the loading state for toggles to prevent multiple clicks
   */
  function updateLoadingToggles(authorId: number, loading: boolean) {
    setLoadingToggles((prev) => ({
      ...prev,
      [authorId]: loading,
    }));
  }

  /**
   * Updates the notification status map for a specific author
   */
  function updateStatusMap(authorId: number, checked: boolean) {
    setNotificationStatusMap((prev) => ({
      ...prev,
      [authorId]: checked,
    }));
  }

  /**
   * Updates the local subscriptions settings state with the modified settings
   */
  function updateSubscriptionsSettings(updatedSettings: SubscriptionSettings) {
    setSubscriptionsSettings((prev) =>
      prev.map((setting) => (setting.id === updatedSettings.id ? updatedSettings : setting))
    );
  }

  /**
   * Gets notification settings for all authors when using a package subscription
   */
  function getPackageSubscriptionSettings() {
    const packageSettings = subscriptionsSettings.find((s) => s.authorId === null);
    const newNotificationStatusMap: Record<string, boolean> = {};

    if (!packageSettings) return newNotificationStatusMap;

    // Apply default enabled state to all authors
    for (const author of authors) {
      newNotificationStatusMap[author.lookupKey] = packageSettings?.notificationSettings?.defaultEnabled || false;
    }

    // Apply specific overrides
    const overrides = packageSettings?.notificationSettings?.overrides || {};
    for (const [authorId, enabled] of Object.entries(overrides)) {
      newNotificationStatusMap[authorId] = enabled;
    }

    return newNotificationStatusMap;
  }

  /**
   * Gets notification settings for each author when not using a package subscription
   */
  function getPerAuthorSubscriptionSettings() {
    const newNotificationStatusMap: Record<string, boolean> = {};

    subscriptionsSettings.forEach((setting) => {
      if (setting.authorId !== null) {
        newNotificationStatusMap[setting.authorId] = setting?.notificationSettings?.defaultEnabled || false;
      }
    });

    return newNotificationStatusMap;
  }

  /**
   * Ensures all authors have a setting in the status map, defaulting to false if not found
   */
  function ensureAllAuthorsHaveSettings(statusMap: Record<string, boolean>) {
    authors.forEach((author) => {
      if (statusMap[author.lookupKey] === undefined) {
        statusMap[author.lookupKey] = false;
      }
    });

    return statusMap;
  }

  /**
   * Handles toggle changes for author notification settings
   * If a user has a package subscription, it updates the package settings.
   * If a user has per-author subscriptions, it updates the author settings.
   * If they have both, it updates both settings.
   */
  async function handleToggleChange(authorId: number, checked: boolean) {
    updateLoadingToggles(authorId, true);

    try {
      const activePackageSubscription = findActivePackageSubscription(subscriptions);
      const activeAuthorSubscription = findActiveAuthorSubscription(subscriptions, authorId);
      const authorSubscriptionSettings = subscriptionsSettings.find((s) => s.id === activeAuthorSubscription?.id);
      const packageSubscriptionSettings = subscriptionsSettings.find((s) => s.id === activePackageSubscription?.id);
      let updatedPackageSettings: SubscriptionSettings | undefined;
      let updatedAuthorSettings: SubscriptionSettings | undefined;

      if (userHasPackageSubscription && packageSubscriptionSettings) {
        updatedPackageSettings = updatePackageSubscriptionSettings(packageSubscriptionSettings, authorId, checked);
      }

      if (authorSubscriptionSettings) {
        updatedAuthorSettings = updateAuthorSubscriptionSettings(authorSubscriptionSettings, checked);
      }

      if (updatedPackageSettings || updatedAuthorSettings) {
        if (updatedPackageSettings) {
          await saveUpdatedSettings(updatedPackageSettings, authorId, checked);
        }

        if (updatedAuthorSettings) {
          await saveUpdatedSettings(updatedAuthorSettings, authorId, checked);
        }
      } else {
        console.error(`No settings found for author ${authorId}`);
        alert('Failed to update notification settings: Settings not found');
      }
    } catch (error) {
      console.error(`Error updating notifications for subscription ${authorId}:`, error);
      alert('Failed to update notification settings');
    } finally {
      updateLoadingToggles(authorId, false);
    }
  }

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const statusMap = userHasPackageSubscription
      ? getPackageSubscriptionSettings()
      : getPerAuthorSubscriptionSettings();

    const completeStatusMap = ensureAllAuthorsHaveSettings(statusMap);

    setNotificationStatusMap(completeStatusMap);
  }, [subscriptionsSettings, userHasPackageSubscription]);

  return {
    subscriptionsSettings,
    notificationStatusMap,
    loadingToggles,
    settingsLoading,
    userHasPackageSubscription,
    handleToggleChange,
  };
}
